import React, { useEffect } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  useNavigate,
} from "react-router-dom";
import { Provider, useDispatch, useSelector } from "react-redux";
import { store } from "./redux/store";
import { SocketProvider } from "./context/SocketContext";
import { setStoreReference } from "./services/api";
import { logout } from "./redux/slices/authSlice";

import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import PrivateRoute from "./components/Auth/PrivateRoute";
import Layout from "./components/Common/Layout"; // ✅ use Layout

// Auth Components
import Login from "./components/Auth/Login";
import ForgetPassword from "./components/Auth/ForgetPassword";
import VerifyOTP from "./components/Auth/VerifyOTP";
import ResetPassword from "./components/Auth/ResetPassword";

// Vendor Components

import Products from "./components/Vendor/Products";
import ProductForm from "./components/Vendor/ProductForm";
import ProductDetails from "./components/Vendor/ProductDetails";

import Vendors from "./components/Admin/Vendors";
import CreateVendor from "./components/Admin/CreateVendor";
import VendorDetails from "./components/Admin/VendorDetails";

// Delivery Profile Components
import DeliveryProfiles from "./components/Admin/DeliveryProfiles";
import DeliveryProfileForm from "./components/Admin/DeliveryProfileForm";
import DeliveryProfileDetails from "./components/Admin/DeliveryProfileDetails";

// Admin Components
import Dashboard from "./components/Admin/Dashboard";
import VendorDashboard from "./components/Vendor/VendorDashboard";
import UsersList from "./components/Admin/UsersList";
import UserForm from "./components/Admin/UserForm";
import AdminOrders from "./components/Admin/Orders";
import OrderDetails from "./components/Admin/OrderDetails";
import PaymentHistory from "./components/Admin/PaymentHistory";
import Profile from "./components/Common/Profile";
import ReturnsList from "./components/Admin/ReturnsList";
import ReturnDetails from "./components/Admin/ReturnDetails";
import Banners from "./components/Admin/Banners";
import UserDetails from "./components/Admin/UserDetails";
import Settings from "./components/Admin/Settings";
import CustomersList from "./components/Admin/CustomersList";
import CustomerDetails from "./components/Admin/CustomerDetails";
import RolesList from "./components/Admin/RoleManagement/RolesList";
import ActivityLogsList from "./components/Admin/ActivityLogs/ActivityLogsList";

// Category Components
import Category from "./components/Vendor/Category";

import CategoryDetails from "./components/Vendor/CategoryDetails";

// SubCategory Components
import SubCategory from "./components/Vendor/SubCategory";

import SubCategoryDetails from "./components/Vendor/SubCategoryDetails";

// Boards Components
import Boards from "./components/Vendor/Boards";

// Classes Components
import Classes from "./components/Vendor/Classes";

// Subjects Components
import Subjects from "./components/Vendor/Subjects";

// College Flow Master Components
import Universities from "./components/Vendor/Universities";
import Courses from "./components/Vendor/Courses";
import Semesters from "./components/Vendor/Semesters";
import CollegeSubjects from "./components/Vendor/CollegeSubjects";

// Competitive Flow Master Components
import ExamCategories from "./components/Vendor/ExamCategories";
import Exams from "./components/Vendor/Exams";
import CompetitiveSubjects from "./components/Vendor/CompetitiveSubjects";

// Inventory Components
import InventoryOverview from "./components/Vendor/InventoryOverview";
import PurchaseOrders from "./components/Vendor/PurchaseOrders";
import PurchaseOrderPage from "./components/Vendor/PurchaseOrderPage";
import PurchaseOrderDetails from "./components/Vendor/PurchaseOrderDetails";
import StockAdjustments from "./components/Vendor/StockAdjustments";

// Report Components
import InventoryReport from "./components/Reports/InventoryReport";
import OrdersReport from "./components/Reports/OrdersReport";
import LowStockProductsReport from "./components/Reports/LowStockProductsReport";
import CancelledOrdersReport from "./components/Reports/CancelledOrdersReport";
import RevenueReport from "./components/Reports/RevenueReport";
import RefundsReport from "./components/Reports/RefundsReport";

import "bootstrap/dist/css/bootstrap.min.css";
import "bootstrap-icons/font/bootstrap-icons.css";
import "./App.css";

// Component to handle token validation and store setup
const AppContent = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { user } = useSelector((state) => state.auth);

  useEffect(() => {
    // Set store reference for api.js to use
    setStoreReference(store);

    // Validate token on app load
    const token = localStorage.getItem("authToken");
    const refreshToken = localStorage.getItem("refreshToken");
    const user = localStorage.getItem("user");

    console.log("🔐 [APP] Token validation on load:", {
      hasToken: !!token,
      hasRefreshToken: !!refreshToken,
      hasUser: !!user,
    });

    if (!token || !user) {
      console.log("🔐 [APP] Missing token or user, clearing state");
      // Clear Redux state if localStorage is empty
      dispatch(logout());
    } else {
      // Check if refreshToken is missing - this is a critical issue
      if (!refreshToken) {
        console.error(
          "❌ [APP] WARNING: authToken exists but refreshToken is missing!"
        );
        console.error(
          "❌ [APP] This will cause refresh token failures. User should re-login."
        );
        // Don't logout immediately, but log the warning
        // The interceptor will handle it when a 401 occurs
      }

      // Validate token by checking if it's expired (basic check)
      try {
        const tokenParts = token.split(".");
        if (tokenParts.length === 3) {
          const payload = JSON.parse(atob(tokenParts[1]));
          const currentTime = Date.now() / 1000;

          // If token is expired, try to refresh if we have refreshToken
          if (payload.exp && payload.exp < currentTime) {
            if (refreshToken) {
              console.log(
                "🔐 [APP] Token expired but refreshToken exists, will refresh on next request"
              );
              // Don't logout - let the interceptor handle the refresh
            } else {
              console.error(
                "❌ [APP] Token expired and no refreshToken, logging out"
              );
              dispatch(logout());
              navigate("/bookedu/login", { replace: true });
            }
          } else {
            console.log("✅ [APP] Token is valid");
          }
        }
      } catch (e) {
        console.error("❌ [APP] Invalid token format:", e);
        // Invalid token format, logout
        dispatch(logout());
        navigate("/bookedu/login", { replace: true });
      }
    }
  }, [dispatch, navigate]);

  return (
    <SocketProvider>
      <ToastContainer
        position="top-right"
        autoClose={3000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="light"
        limit={5}
      />
      <div className="App">
        <Routes>
          {/* Auth routes (without layout) */}
          <Route path="/login" element={<Login />} />
          <Route path="/forget-password" element={<ForgetPassword />} />
          <Route path="/verify-otp" element={<VerifyOTP />} />
          <Route path="/reset-password" element={<ResetPassword />} />

          {/* Main app routes with sidebar + header + outlet */}
          <Route path="/" element={<Layout />}>
            <Route
              path="profile"
              element={
                <PrivateRoute
                  allowedRoles={[
                    "admin",
                    "super_admin",
                    "vendor",
                    "customer",
                    "delivery",
                  ]}
                >
                  <Profile />
                </PrivateRoute>
              }
            />
            {/* ------------------Vendor Routes ------------------ */}

            <Route
              path="admin/vendors"
              element={
                <PrivateRoute allowedRoles={["admin", "super_admin", "vendor"]}>
                  <Vendors />
                </PrivateRoute>
              }
            />
            <Route
              path="admin/vendors/create"
              element={
                <PrivateRoute allowedRoles={["admin", "super_admin", "vendor"]}>
                  <CreateVendor />
                </PrivateRoute>
              }
            />
            <Route
              path="admin/vendors/edit/:id"
              element={
                <PrivateRoute allowedRoles={["admin", "super_admin", "vendor"]}>
                  <CreateVendor />
                </PrivateRoute>
              }
            />
            <Route
              path="admin/vendors/details/:id"
              element={
                <PrivateRoute allowedRoles={["admin", "super_admin", "vendor"]}>
                  <VendorDetails />
                </PrivateRoute>
              }
            />

            {/* ------------------Delivery Profile Routes ------------------ */}
            <Route
              path="admin/delivery-profiles"
              element={
                <PrivateRoute allowedRoles={["admin", "super_admin", "vendor"]}>
                  <DeliveryProfiles />
                </PrivateRoute>
              }
            />
            <Route
              path="admin/delivery-profiles/create"
              element={
                <PrivateRoute allowedRoles={["admin", "super_admin", "vendor"]}>
                  <DeliveryProfileForm />
                </PrivateRoute>
              }
            />
            <Route
              path="admin/delivery-profiles/edit/:id"
              element={
                <PrivateRoute allowedRoles={["admin", "super_admin", "vendor"]}>
                  <DeliveryProfileForm />
                </PrivateRoute>
              }
            />
            <Route
              path="admin/delivery-profiles/details/:id"
              element={
                <PrivateRoute allowedRoles={["admin", "super_admin", "vendor"]}>
                  <DeliveryProfileDetails />
                </PrivateRoute>
              }
            />

            {/* Product Routes */}
            <Route
              path="/product"
              element={
                <PrivateRoute allowedRoles={["admin", "super_admin", "vendor"]}>
                  <Products />
                </PrivateRoute>
              }
            />
            <Route
              path="/product/create"
              element={
                <PrivateRoute allowedRoles={["admin", "super_admin", "vendor"]}>
                  <ProductForm />
                </PrivateRoute>
              }
            />
            <Route
              path="/product/edit/:id"
              element={
                <PrivateRoute allowedRoles={["admin", "super_admin", "vendor"]}>
                  <ProductForm />
                </PrivateRoute>
              }
            />
            <Route
              path="/product/details/:id"
              element={
                <PrivateRoute allowedRoles={["admin", "super_admin", "vendor"]}>
                  <ProductDetails />
                </PrivateRoute>
              }
            />

            {/* Legacy Vendor Product Routes */}
            {/* <Route
                path="vendor/products"
                element={
                  <PrivateRoute allowedRoles={["vendor"]}>
                    <Products />
                  </PrivateRoute>
                }
              /> */}

            {/* <Route
                path="vendor/add-product"
                element={
                  <PrivateRoute allowedRoles={["vendor"]}>
                    <AddProduct />
                  </PrivateRoute>
                }
              /> */}

            {/* Category Routes */}
            <Route
              path="/category"
              element={
                <PrivateRoute allowedRoles={["admin", "super_admin", "vendor"]}>
                  <Category />
                </PrivateRoute>
              }
            />

            <Route
              path="/category/details/:id"
              element={
                <PrivateRoute allowedRoles={["admin", "super_admin", "vendor"]}>
                  <CategoryDetails />
                </PrivateRoute>
              }
            />

            {/* SubCategory Routes */}
            <Route
              path="/sub-category"
              element={
                <PrivateRoute allowedRoles={["admin", "super_admin", "vendor"]}>
                  <SubCategory />
                </PrivateRoute>
              }
            />

            <Route
              path="/sub-category/details/:id"
              element={
                <PrivateRoute allowedRoles={["admin", "super_admin", "vendor"]}>
                  <SubCategoryDetails />
                </PrivateRoute>
              }
            />

            <Route
              path="/boards"
              element={
                <PrivateRoute allowedRoles={["admin", "super_admin", "vendor"]}>
                  <Boards />
                </PrivateRoute>
              }
            />

            <Route
              path="/classes"
              element={
                <PrivateRoute allowedRoles={["admin", "super_admin", "vendor"]}>
                  <Classes />
                </PrivateRoute>
              }
            />

            <Route
              path="/subjects"
              element={
                <PrivateRoute allowedRoles={["admin", "super_admin", "vendor"]}>
                  <Subjects />
                </PrivateRoute>
              }
            />

            <Route
              path="/universities"
              element={
                <PrivateRoute allowedRoles={["admin", "super_admin", "vendor"]}>
                  <Universities />
                </PrivateRoute>
              }
            />

            <Route
              path="/courses"
              element={
                <PrivateRoute allowedRoles={["admin", "super_admin", "vendor"]}>
                  <Courses />
                </PrivateRoute>
              }
            />

            <Route
              path="/semesters"
              element={
                <PrivateRoute allowedRoles={["admin", "super_admin", "vendor"]}>
                  <Semesters />
                </PrivateRoute>
              }
            />

            <Route
              path="/college-subjects"
              element={
                <PrivateRoute allowedRoles={["admin", "super_admin", "vendor"]}>
                  <CollegeSubjects />
                </PrivateRoute>
              }
            />

            {/* Competitive Master Routes */}
            <Route
              path="/exam-categories"
              element={
                <PrivateRoute allowedRoles={["admin", "super_admin", "vendor"]}>
                  <ExamCategories />
                </PrivateRoute>
              }
            />
            <Route
              path="/exams"
              element={
                <PrivateRoute allowedRoles={["admin", "super_admin", "vendor"]}>
                  <Exams />
                </PrivateRoute>
              }
            />
            <Route
              path="/competitive-subjects"
              element={
                <PrivateRoute allowedRoles={["admin", "super_admin", "vendor"]}>
                  <CompetitiveSubjects />
                </PrivateRoute>
              }
            />

            {/* Inventory Management Routes */}
            <Route
              path="/inventory"
              element={
                <PrivateRoute allowedRoles={["admin", "super_admin", "vendor"]}>
                  <InventoryOverview />
                </PrivateRoute>
              }
            />
            <Route
              path="/purchase-orders"
              element={
                <PrivateRoute allowedRoles={["admin", "super_admin", "vendor"]}>
                  <PurchaseOrders />
                </PrivateRoute>
              }
            />
            <Route
              path="/purchase-orders/create"
              element={
                <PrivateRoute allowedRoles={["admin", "super_admin", "vendor"]}>
                  <PurchaseOrderPage />
                </PrivateRoute>
              }
            />
            <Route
              path="/purchase-orders/edit/:id"
              element={
                <PrivateRoute allowedRoles={["admin", "super_admin", "vendor"]}>
                  <PurchaseOrderPage />
                </PrivateRoute>
              }
            />
            <Route
              path="/purchase-orders/details/:id"
              element={
                <PrivateRoute allowedRoles={["admin", "super_admin", "vendor"]}>
                  <PurchaseOrderDetails />
                </PrivateRoute>
              }
            />
            <Route
              path="/stock-adjustments"
              element={
                <PrivateRoute allowedRoles={["admin", "super_admin", "vendor"]}>
                  <StockAdjustments />
                </PrivateRoute>
              }
            />

            {/* Admin Routes */}
            <Route
              path="/"
              element={
                <PrivateRoute allowedRoles={["admin", "super_admin", "vendor"]}>
                  {user && user.user_type === "vendor" ? (
                    <VendorDashboard />
                  ) : (
                    <Dashboard />
                  )}
                </PrivateRoute>
              }
            />
            <Route
              path="admin/users"
              element={
                <PrivateRoute allowedRoles={["admin", "super_admin", "vendor"]}>
                  <UsersList />
                </PrivateRoute>
              }
            />
            <Route
              path="admin/users/new"
              element={
                <PrivateRoute allowedRoles={["admin", "super_admin", "vendor"]}>
                  <UserForm />
                </PrivateRoute>
              }
            />
            <Route
              path="admin/users/:id/edit"
              element={
                <PrivateRoute allowedRoles={["admin", "super_admin", "vendor"]}>
                  <UserForm />
                </PrivateRoute>
              }
            />
            <Route
              path="admin/users/details/:id"
              element={
                <PrivateRoute allowedRoles={["admin", "super_admin", "vendor"]}>
                  <UserDetails />
                </PrivateRoute>
              }
            />
            <Route
              path="admin/roles"
              element={
                <PrivateRoute allowedRoles={["admin", "super_admin"]}>
                  <RolesList />
                </PrivateRoute>
              }
            />
            <Route
              path="admin/activity-logs"
              element={
                <PrivateRoute allowedRoles={["admin", "super_admin"]}>
                  <ActivityLogsList />
                </PrivateRoute>
              }
            />
            {/* Customer Routes */}
            <Route
              path="admin/customers"
              element={
                <PrivateRoute allowedRoles={["admin", "super_admin", "vendor"]}>
                  <CustomersList />
                </PrivateRoute>
              }
            />
            <Route
              path="admin/customers/details/:id"
              element={
                <PrivateRoute allowedRoles={["admin", "super_admin", "vendor"]}>
                  <CustomerDetails />
                </PrivateRoute>
              }
            />
            <Route
              path="admin/orders"
              element={
                <PrivateRoute allowedRoles={["admin", "super_admin", "vendor"]}>
                  <AdminOrders />
                </PrivateRoute>
              }
            />
            <Route
              path="admin/orders/details/:id"
              element={
                <PrivateRoute allowedRoles={["admin", "super_admin", "vendor"]}>
                  <OrderDetails />
                </PrivateRoute>
              }
            />
            <Route
              path="admin/returns"
              element={
                <PrivateRoute allowedRoles={["admin", "super_admin", "vendor"]}>
                  <ReturnsList />
                </PrivateRoute>
              }
            />
            <Route
              path="admin/returns/details/:id"
              element={
                <PrivateRoute allowedRoles={["admin", "super_admin", "vendor"]}>
                  <ReturnDetails />
                </PrivateRoute>
              }
            />
            <Route
              path="admin/payments"
              element={
                <PrivateRoute allowedRoles={["admin", "super_admin", "vendor"]}>
                  <PaymentHistory />
                </PrivateRoute>
              }
            />
            <Route
              path="admin/settings"
              element={
                <PrivateRoute allowedRoles={["admin", "super_admin", "vendor"]}>
                  <Settings />
                </PrivateRoute>
              }
            />

            {/* Banner Routes */}
            <Route
              path="banners"
              element={
                <PrivateRoute allowedRoles={["admin", "super_admin", "vendor"]}>
                  <Banners />
                </PrivateRoute>
              }
            />
            {/* Report Routes */}
            <Route
              path="reports/inventory"
              element={
                <PrivateRoute
                  allowedRoles={["admin", "super_admin", "vendor", "delivery"]}
                >
                  <InventoryReport />
                </PrivateRoute>
              }
            />
            <Route
              path="reports/orders"
              element={
                <PrivateRoute
                  allowedRoles={["admin", "super_admin", "vendor", "delivery"]}
                >
                  <OrdersReport />
                </PrivateRoute>
              }
            />
            <Route
              path="reports/cancelled-orders"
              element={
                <PrivateRoute
                  allowedRoles={["admin", "super_admin", "vendor", "delivery"]}
                >
                  <CancelledOrdersReport />
                </PrivateRoute>
              }
            />
            <Route
              path="reports/low-stock"
              element={
                <PrivateRoute
                  allowedRoles={["admin", "super_admin", "vendor", "delivery"]}
                >
                  <LowStockProductsReport />
                </PrivateRoute>
              }
            />
            <Route
              path="reports/revenue"
              element={
                <PrivateRoute
                  allowedRoles={["admin", "super_admin", "vendor", "delivery"]}
                >
                  <RevenueReport />
                </PrivateRoute>
              }
            />
            <Route
              path="admin/refunds"
              element={
                <PrivateRoute allowedRoles={["admin", "super_admin", "vendor"]}>
                  <RefundsReport />
                </PrivateRoute>
              }
            />
          </Route>

          {/* Catch all */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
        {/* <Footer /> */}
      </div>
    </SocketProvider>
  );
};

function App() {
  return (
    <Provider store={store}>
      <Router basename="/bookedu">
        <AppContent />
      </Router>
    </Provider>
  );
}

export default App;
